/**
 * Player-step-specific literals.
 *
 * These are the raw colour / gradient / timing values that ONLY the Players
 * step uses, ported from the standalone app's `features/players/OptionCard.tsx`,
 * `features/features/HighlightsExperience.tsx`, `VideoStage.tsx` and the
 * `highlights.css` reveal animation. They re-use the shared `CFG` palette where
 * possible (so this file holds only what is genuinely player-local) and exist so
 * the step's styled-components carry no raw literals.
 *
 * NOTE: the per-tag `logo.bg` / `logo.color` in the mock data are DATA values
 * (source-brand colours) and are NOT here — they arrive as props.
 */
import { owsVar, themed } from '../../../../theme/themeRegistry';
import { CFG } from '../../../../theme/configurator-tokens';

// OptionCard ------------------------------------------------------------------
// The redesigned option cards mirror the Design step's ThemeCard: a rounded
// icon tile (holding the option's API art) at left + an OPTION label / title,
// selected as a larger card with a brand border and a warm corner glow.
export const PLAYERS = themed('cfgPlayers', {
  // Rounded art-tile backdrop behind each option's image (Figma zinc/900).
  iconTile: '#18181b',
  // Unselected card hairline border (Figma white/10).
  cardBorderSoft: 'rgba(255, 255, 255, 0.1)',
  // "Recommended" pill fill (Figma flat/default-flat = zinc/700 @ 40%). Kept for
  // the currently-hidden pill so re-enabling it later needs no token work.
  pillFlat: 'rgba(63, 63, 70, 0.4)',
} as const);

// Selected-card background — a warm orange corner glow (bottom-right) read
// through a dark left→clear legibility scrim, so the OPTION label + title stay
// crisp while the brand glow bleeds in behind the art. Ported from the Figma
// layers (the radial wash + two stacked dark overlays).
export const PLAYERS_GRADIENTS = themed('cfgPlayersGradients', {
  // Bottom-right radial brand glow (Figma radial @ 48% opacity, alpha pre-baked).
  selectedWash:
    'radial-gradient(120% 120% at 100% 100%, rgba(255,121,27,0.48) 0%, rgba(191,91,20,0.36) 33%, rgba(128,61,14,0.24) 55%, rgba(64,30,7,0.12) 78%, rgba(0,0,0,0) 100%)',
  // Dark left→clear scrim (two stacked Figma overlays) laid over the glow.
  selectedOverlay:
    'linear-gradient(111deg, rgba(17,16,21,0.97) 66%, rgba(26,26,26,0) 93%),' +
    'linear-gradient(87deg, rgba(17,16,21,0.9) 26%, rgba(26,26,26,0) 100%)',
} as const);

// V4 promo step — the left "GO VIRAL / We bring the tools" hero + $100 advertising
// credit card, and the right-panel "Promote your brand" placeholder. Only the
// literals not already in CFG live here (goViral=CFG.forceDark, labels=CFG.base400,
// amount=CFG.brand are used straight from CFG in the styles).
export const PROMO = themed('cfgPlayersPromo', {
  // Credit-card overlay inks.
  creditDollar: 'rgba(255, 121, 27, 0.72)', // the "$" sign (brand @ 72%)
  afterCheckout: 'rgba(255, 255, 255, 0.8)', // "after checkout" caption
  // Light that travels the wallet border once, after the reveal (the center card's
  // orange border shine — primary brand orange). The handoff card uses the warm
  // star border (PROMO_RAYS_COLOR) instead.
  shine: CFG.brand,
  // Right-panel placeholder box fill (Figma zinc/900 under a black 24% scrim).
  placeholderBg: '#121215',
  // Globe-icon tint in the collapsed "Web Ads" responsive label (gradient-start cyan,
  // matching the weBring gradient the label text uses).
  webAds: '#7ec7ff',
  // "In-app Ads" tool: mobile-icon tint (gradient-start mint). The phone-screen text
  // inks reuse CFG (foreground / base300) so they follow theming.
  inAppAds: '#adfde0', // mobile-icon tint (mint, matches the inAppAds gradient start)
  // Soft emerald glow under the "FREE" stamp on the envelope sleeve (premium sheen).
  freeGlow: 'rgba(16, 185, 129, 0.45)',
  // Tool-label icon tint (purple/lavender) = the gradient-start of the label wordmark.
  // Shared by all six Promo Branding Tools labels (Figma 9471-112304).
  toolLabelIcon: '#aea6f1',
  // "GO VIRAL" eyebrow (lavender = the mid-stop of the headline gradient).
  goViral: '#cec1cc',
  // Dim scrim behind the "app opening" backdrop-blur on the In-app Ads phone.
  blurScrim: 'rgba(6, 6, 7, 0.2)',
  // Black 24% scrim laid over the whole promo stage's left area (behind the headline, the
  // logo + the account menu): a flat dim that darkens the tools marquee WITHOUT making it
  // translucent — `opacity` on the tools would let the page show through instead.
  stageScrim: 'rgba(0, 0, 0, 0.24)',
  // "Streaming is starting" overlay text centred on the Influencers clip (Figma 9516-24182).
  influencerText: '#d234b8',
} as const);

// "Promo Branding Tools" — the browser-window mockup that spawns in the centre
// (Figma node 9430-93823). Zinc-scale chrome + macOS traffic-light dots.
export const PROMO_BROWSER = themed('cfgPlayersBrowser', {
  bg: '#18181b', // window body + control bar (zinc/900)
  tab: '#27272a', // the active tab (zinc/800)
  url: '#3f3f46', // URL-bar fill (zinc/700)
  block: '#262728', // skeleton hero block + cards (surface-tertiary)
  line: '#2c2c31', // skeleton text lines
  border: 'rgba(255, 255, 255, 0.06)', // hairline around the window + cards
  tabText: '#e4e4e7', // tab label (zinc/200)
  urlText: '#a1a1aa', // domain text (zinc/400)
  trafficRed: '#ff5f57',
  trafficYellow: '#febc2e',
  trafficGreen: '#28c840',
} as const);

// LightRays shader colour. Passed to WebGL (`hexToRgb` in the LightRays shader),
// which can't resolve a `var(--ows-…)` custom property — so, like CFG_STRIPE, this
// stays a plain hex literal rather than going through themed().
export const PROMO_RAYS_COLOR = '#ffe3d1';

// RippleGrid shader colour (the animated grid on the credit-card face). Same story
// as PROMO_RAYS_COLOR: it's a `hexToRgb` uniform, so it can't be a themed CSS var.
export const PROMO_RIPPLE_GRID_COLOR = '#7ec7ff';

// Star-border glow colour — the animated edge shine that sweeps the top/bottom of the
// HANDED-OFF sidebar card only. Cyan (the "$100" amount's gradient-start) so the sweep reads
// in the amount's colour family. Standalone (NOT PROMO_RAYS_COLOR) so the Web Ads LightRays,
// which share that warm hue, stay warm.
export const PROMO_STAR_COLOR = '#7ec7ff';

export const PROMO_GRADIENTS = themed('cfgPlayersPromoGradients', {
  // "We bring the tools" gradient text (cyan → mint). Also the $100 amount (Figma 9526-24846).
  weBring: 'linear-gradient(90deg, #7ec7ff 0%, #adffda 100%)',
  // The "FREE" stamp on the envelope sleeve: a premium emerald gradient (bright mint
  // top → rich emerald). Green (not the amount's cyan → mint) so the giveaway word
  // reads as its own accent, set apart from the "$100".
  freeStamp: 'linear-gradient(180deg, #6ee7b7 0%, #34d399 45%, #10b981 100%)',
  // "Start Promoting Today" headline (purple → lavender → orange, 72% alpha). Figma 9430-93697.
  startPromoting:
    'linear-gradient(90deg, rgba(174,166,241,0.72) 0%, rgba(206,193,204,0.72) 50%, rgba(218,156,116,0.72) 100%)',
  // The six Promo Branding Tools label wordmarks (purple → lavender → orange, full alpha).
  // Same hues as the headline but crisper (Figma 9471-112304).
  toolLabel: 'linear-gradient(90deg, #aea6f1 0%, #cec1cc 50%, #da9c74 100%)',
  // Headline divider: the "Start Promoting Today" hues (purple → lavender → orange) fading to
  // transparent at both ends (keeps the styled divider's fade, just recoloured).
  dividerStartPromoting:
    'linear-gradient(90deg, rgba(174,166,241,0) 0%, #aea6f1 25%, #cec1cc 50%, #da9c74 75%, rgba(218,156,116,0) 100%)',
  // Light band swept once across the gradient text on entrance (the shimmer).
  weBringShimmer:
    'linear-gradient(100deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 70%)',
  // Wallet back (the container behind the sliding card): subtle top-left sheen
  // fading to zinc/900, ported from the card's outer radial.
  walletBg: 'radial-gradient(120% 120% at 15% 0%, #48494e 0%, #18181b 62%)',
  // "In-app Ads" tool accent (mint → cyan): the label wordmark + the ad progress fill.
  inAppAds: 'linear-gradient(90deg, #adfde0 0%, #7fc8fd 100%)',
  // Left+right edge scrim over the tools marquee (Figma 9506-944 "overlay"): the
  // scrolling tools dissolve into the dark stage at both horizontal edges instead of
  // hard-clipping. Dark at each edge (#0b0a0c @ 92%), clear through the middle
  // (~20% fade per side). The transparent stops use the edge ink at 0 alpha (not
  // transparent black) so the fade stays clean — matching Figma's premultiplied render.
  toolsEdgeScrim:
    'linear-gradient(270deg, rgba(11,10,12,0.92) 0%, rgba(11,10,12,0) 19.805%, rgba(11,10,12,0) 80%, rgba(11,10,12,0.92) 100%)',
} as const);

/**
 * Keep-dark re-pin for the advertising-credit "wallet" (CreditCard). The card is a
 * deliberately DARK object (dark chrome art + cyan→mint amount + emerald FREE stamp), so
 * it must render identically in the LIGHT theme — otherwise the generic light derivation
 * flips its back to white and mutes the amount/caption. Re-declares every themed var the
 * wallet subtree consumes to its DARK value; CreditCard.styles applies it SCOPED to the
 * wallet root (`:root[data-ows-theme='light'] &`), so the vars it SHARES with other
 * surfaces stay light outside the card (weBringShimmer → the PromoStage headline shimmer;
 * base400 → body text everywhere). Var names come from `owsVar` so they track the tokens
 * above; the dark values MUST mirror those tokens (PROMO / PROMO_GRADIENTS) + CFG.base400.
 */
export const WALLET_KEEP_DARK = `
  ${owsVar('cfgPlayersPromoGradients', 'walletBg')}: radial-gradient(120% 120% at 15% 0%, #48494e 0%, #18181b 62%);
  ${owsVar('cfgPlayersPromoGradients', 'weBring')}: linear-gradient(90deg, #7ec7ff 0%, #adffda 100%);
  ${owsVar(
    'cfgPlayersPromoGradients',
    'weBringShimmer',
  )}: linear-gradient(100deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 70%);
  ${owsVar(
    'cfgPlayersPromoGradients',
    'freeStamp',
  )}: linear-gradient(180deg, #6ee7b7 0%, #34d399 45%, #10b981 100%);
  ${owsVar('cfgPlayersPromo', 'afterCheckout')}: rgba(255, 255, 255, 0.8);
  ${owsVar('cfgPlayersPromo', 'freeGlow')}: rgba(16, 185, 129, 0.45);
  ${owsVar('cfg', 'base400')}: #a1a1aa;
`;

/**
 * V4 promo-step animation timings — durations, delays and easings. Kept in this
 * *tokens.ts home so the styled-components carry no raw `Ns`/easing literals (the
 * nova area rule). Values are the tuned originals, unchanged.
 */
export const PROMO_ANIM = {
  // Easings.
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)', // headline reveal + button transform
  cardSlide: 'cubic-bezier(0.25, 1, 0.3, 1)', // card reveal / hover peek slide
  exitIn: 'cubic-bezier(0.4, 0, 1, 1)', // hand-off exit
  landIn: 'cubic-bezier(0.22, 1, 0.36, 1)', // sidebar card entrance
  // Durations.
  cardOpen: '0.85s', // center card reveal slide
  cardPeek: '0.45s', // handoff hover slide
  amountFade: '0.45s', // amount + caption fade-in
  star: '8s', // star-border loop
  shine: '1.15s', // label + headline shimmer sweep
  borderShine: '1.6s', // orange border-shine pass
  headlineIn: '0.6s', // headline reveal
  exit: '0.6s', // hand-off card exit
  fade: '0.5s', // divider / "+" / button fade
  phraseOut: '0.35s', // rotating-headline fade-out before the phrase swaps
  plusOut: '0.4s', // "+" fade-out on hand-off
  photoFade: '0.9s', // hero photo fade-in
  cardEnter: '0.55s', // sidebar card entrance
  collapse: '0.7s', // web frame responsive shrink (reveals the "Web Ads" label)
  marquee: '32s', // one seamless loop of the tools ticker (translateX 0 → -50%)
  coinFloat: '6s', // one idle-float cycle of a Crypto Sites coin
  floatEase: 'ease-in-out', // idle-float easing (organic ease at the turns)
  hover: '0.2s', // button bg/border hover
  none: '0s', // no-delay branch
  // Delays.
  dHeadline: '0.1s', // GO VIRAL reveal
  dDivider: '0.2s', // divider fade
  dWeBring: '0.28s', // "Start Promoting Today" reveal
  dShimmer: '1s', // headline shimmer
  dPlus: '0.5s', // "+" fade-in
  dAmount: '0.35s', // amount fade
  dCaption: '0.42s', // caption fade
  dAmountShine: '1.3s', // amount shimmer first sweep, after the count-up settles
  amountShineLoop: '3.4s', // amount shimmer loop period (1.15s sweep + rest); kept lively on its own rhythm (the headline now rotates every 8s)
  dWebAds: '0.35s', // "Web Ads" label fade-in (after the frame has shrunk enough to show it)
  // In-app Ads phone "open the app" assembly (fires 64px before the phone centres).
  // Timings scaled ~2.08x from the original 1.85s sequence so the whole reveal now runs
  // ~3.85s (+2s) at the SAME proportions, slow enough to follow as the phone crosses centre.
  appIconPop: '0.73s', // (a) the "App" dock icon grows + brightens
  appOpen: '1.56s', // (b) the app panel zooms open from the icon (+ blur behind)
  appContentIn: '1.04s', // (c) "YOUR AD HERE" + the brand banner fade in
  appProgress: '1.87s', // (d) the ad progress bar fills to 100%
  dAppOpen: '0.12s', // panel zoom starts almost with the icon pop (so it reads before centre)
  dAppContent: '1.04s', // content fades in once the panel is (near) full-screen
  dAppProgress: '1.98s', // progress fills last
  // Video Ads playback: the play icon becomes pause + fades out, then the bar + timestamps
  // run 0:02 → 0:14.
  videoPlay: '2s', // the ad-video progress + timestamps run
  videoCross: '0.35s', // play → pause crossfade; when it ends, the fade-out + progress start
  videoIcon: '1.5s', // play → pause → the pause fading out (total)
} as const;

// Assembled gradient / mask literals for the credit card, built from the tokens
// above so the component files stay literal-free. PROMO.shine / CFG.base400 carry
// their own theme vars; PROMO_RAYS_COLOR is the fixed warm (WebGL-parity) hue.
/** Cyan radial glow behind each star-border sliver — the sidebar card's animated edge
 *  shine, tinted to the "$100" amount (see PROMO_STAR_COLOR). */
export const PROMO_STAR_GLOW = `radial-gradient(circle, ${PROMO_STAR_COLOR}, transparent 10%)`;
/** Arc that sweeps the marquee tool borders (angle animated via --ows-promo-shine-angle).
 *  Tinted to the in-app-ads progress gradient (the `toolLabel` hues, purple → lavender →
 *  orange) instead of a flat orange. */
export const PROMO_BORDER_SHINE =
  'conic-gradient(from var(--ows-promo-shine-angle), transparent 0%, transparent 66%, #aea6f1 76%, #cec1cc 82%, #da9c74 88%, transparent 92%)';
/** Wallet-card border shine, tinted cyan → mint to match the $100 amount (Figma) instead of
 *  the marquee's orange arc. */
export const PROMO_CARD_BORDER_SHINE =
  'conic-gradient(from var(--ows-promo-shine-angle), transparent 0%, transparent 66%, #7ec7ff 78%, #adffda 86%, transparent 92%)';
/** Solid base400 ink read through background-clip:text for the card label. */
export const PROMO_LABEL_INK = `linear-gradient(${CFG.base400}, ${CFG.base400})`;
/** mask-composite border-ring mask (theme-invariant — #000 is just the mask fill). */
export const PROMO_RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)';
/** Drop shadow under the "opening app" panel (Figma 9506-816). */
export const PROMO_APP_SHADOW = '4px 4px 6.2px 10px rgba(0, 0, 0, 0.25)';

// HighlightsExperience --------------------------------------------------------
export const HL = themed('cfgPlayersHl', {
  // Card / pill surfaces (from highlights.css --hl-* custom props).
  cardBg: '#111014',
  pillBg: '#2c2c2e',
  // Apple-ease for the pill <-> expanded card morph.
  appleEase: 'cubic-bezier(0.32, 0.72, 0, 1)',
  // Description colour + animated shine sweep.
  descText: '#cccccc',
  descShine: 'linear-gradient(120deg, #ccc 0%, #ccc 40%, #fff 50%, #ccc 60%, #ccc 100%)',
  groupTitle: '#8e8e93',
  // The "is-loading" pill conic spinner border.
  loadingBorder:
    'conic-gradient(from 0deg, transparent 0deg, transparent 60deg, #00e0ff 120deg, #8070e9 180deg, #ff00d4 240deg, transparent 360deg)',
  // Animated check.
  checkGreen: CFG.success,
  checkWhite: '#ffffff',
  // Stacked "searching" avatar fills (decorative source logos).
  stack1: '#e72b02',
  stack2: '#222222',
  stack3: '#1d2b44',
  searchingText: CFG.base400,
  searchingFinal: '#ffffff',
  // Centre stage radial glow + video drop shadow.
  stageGlow: 'radial-gradient(circle at center, rgba(255,123,0,0.04) 0%, transparent 60%)',
  videoShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.6)',
  pillLogoFallback: '#ffffff',
} as const);

/** Equal edge-fade size (px) on all four sides of the demo clip — matches the
 *  Features step so both stages dissolve their black edges into the page alike. */
export const VIDEO_EDGE_FADE_PX = 80;

/** 4-side equal edge fade as an ALPHA mask (theme-invariant — black = opaque,
 *  transparent = cut out), used as BOTH `mask` and `-webkit-mask` with
 *  `intersect` / `source-in`. Mirrors the Features step (kept page-local to avoid a
 *  cross-step import). Only acts where the clip actually reaches an edge. */
export const VIDEO_EDGE_MASK =
  `linear-gradient(to right, transparent 0, #000 ${VIDEO_EDGE_FADE_PX}px, #000 calc(100% - ${VIDEO_EDGE_FADE_PX}px), transparent 100%),` +
  `linear-gradient(to bottom, transparent 0, #000 ${VIDEO_EDGE_FADE_PX}px, #000 calc(100% - ${VIDEO_EDGE_FADE_PX}px), transparent 100%)`;

/** Wider edge feather for the full-width static image. It fills the whole stage width,
 *  so a softer, larger fade blends it into the page better than the clip's tighter one.
 *  Same 4-side alpha-mask shape as VIDEO_EDGE_MASK. */
export const IMAGE_EDGE_FADE_PX = 148;

export const IMAGE_EDGE_MASK =
  `linear-gradient(to right, transparent 0, #000 ${IMAGE_EDGE_FADE_PX}px, #000 calc(100% - ${IMAGE_EDGE_FADE_PX}px), transparent 100%),` +
  `linear-gradient(to bottom, transparent 0, #000 ${IMAGE_EDGE_FADE_PX}px, #000 calc(100% - ${IMAGE_EDGE_FADE_PX}px), transparent 100%)`;

// Reveal timings (ms) — ported from `features/features/reveal.ts`. ------------
export const HL_TIMING = {
  /** Pause before the description starts typing. */
  preType: 805,
  /** Per-char typing speed of the description. */
  descCharMs: 17,
  /** Per-char typing speed of a tag pill's label. */
  pillCharMs: 23,
  /** Grouped pills: delay before a pill starts typing / after it finishes. */
  groupPillPre: 288,
  groupPillPost: 115,
  /** Ungrouped pills: delay before / after. */
  tagPillPre: 345,
  tagPillPost: 173,
  /** Searching pill: interval between count steps + final settle. */
  searchStep: 805,
  searchSettle: 345,
  /** Pause after a highlight finishes revealing before auto-advancing. */
  advancePause: 1725,
} as const;

/**
 * Searching-pill count-up steps shown in sequence. These are the decorative
 * fallback COUNTS only (the data value); the "+N more" label is built at the
 * render hook via t('+{{n}} more', { n }) so the "more" copy is
 * translatable while the number stays data.
 */
export const HL_SEARCH_STEPS = [3, 20, 77, 114] as const;
/** Final "+N more" count after the searching sequence settles. */
export const HL_SEARCH_FINAL = 114;
